import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid,
  Card,
  CardActionArea,
  Alert,
  Checkbox,
  Button,
  Fab,
  Tooltip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import Layout from '../components/Layout';
import LoadingOverlay from '../components/LoadingOverlay';
import { placeholderImage, getBestItemImageUrl } from '../utils/imageUtils';
import itemService from '../services/itemService';
import api, { directApi } from '../services/api';
import EnhancedSearchBar from '../components/EnhancedSearchBar';
import ItemDetailModal from '../components/ItemDetailModal';
import demoService from '../services/demoService';

// Choose the appropriate API instance based on environment
const apiInstance = process.env.REACT_APP_API_URL ? directApi : api;

const AllItems = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  
  // AI search related states
  const [searchTier, setSearchTier] = useState('keyword');
  const [searchTime, setSearchTime] = useState(null);
  const [showAiSearchOption, setShowAiSearchOption] = useState(false);
  const [lastSearchQuery, setLastSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  
  // Item detail modal states
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [itemDetailOpen, setItemDetailOpen] = useState(false);
  
  // Fetch all items function - extracted for reuse
  const fetchItems = async () => {
    try {
      setIsLoading(true);
      // Fetch all items - use high limit to get all items for Pro users
      // Backend will handle plan-based restrictions if needed
      const response = await itemService.getAllItems({ limit: 10000 });
      console.log('Fetched items:', response);
      setItems(response || []);
      setFilteredItems(response || []);
      setError(null);
    } catch (error) {
      console.error('Error fetching items:', error);
      setError('Failed to load items. Please try again.');
      // Don't allow errors to cause redirection
      // Just show empty state with error message
      setItems([]);
      setFilteredItems([]);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Mark tags demo feature as completed when page loads
  useEffect(() => {
    if (demoService.isDemoMode()) {
      demoService.markFeatureCompleted('tags');
    }
  }, []);

  // Fetch all items on component mount
  useEffect(() => {
    fetchItems();
  }, []);
  
  // Handle search input change
  const handleSearchChange = (e) => {
    // EnhancedSearchBar might pass the value directly or as an event object
    const value = e && e.target ? e.target.value : e;
    setSearchQuery(value);
    
    // If search is empty, reset to show all items
    if (!value || (typeof value === 'string' && !value.trim())) {
      setFilteredItems(items);
      setSearchTier('keyword');
      setSearchTime(null);
      setShowAiSearchOption(false);
    }
  };
  
  // Handle basic search (keyword-based)
  const handleBasicSearch = async () => {
    if (!searchQuery.trim()) {
      setFilteredItems(items);
      setSearchTier('keyword');
      setSearchTime(null);
      return;
    }
    
    try {
      setIsLoading(true);
      setIsSearching(true);
      setShowAiSearchOption(false); // Reset AI option initially
      setLastSearchQuery(searchQuery); // Store query for possible AI search later
      
      const startTime = performance.now();
      
      // First try to use the backend search API
      try {
        const response = await apiInstance.get(`/search?q=${encodeURIComponent(searchQuery)}`);
        
        if (response.data && Array.isArray(response.data.items)) {
          console.log('Search API returned items:', response.data.items);
          
          // Update search tier
          setSearchTier('llm');
          
          // Update filtered items
          setFilteredItems(response.data.items);
          const endTime = performance.now();
          setSearchTime(Math.round(endTime - startTime));
          
          // Always show AI search option after basic search
          setShowAiSearchOption(true);
          
          setIsLoading(false);
          setIsSearching(false);
          return;
        }
      } catch (apiError) {
        console.warn('API search failed, falling back to local search:', apiError);
      }
      
      // Fallback to local search if API fails
      console.log('Using local search for items');
      setSearchTier('keyword'); // Local search is always keyword-based
      const query = searchQuery.toLowerCase();
      
      // Filter items that match the query
      const filtered = items.filter(item => {
        return (
          (item.name && item.name.toLowerCase().includes(query)) ||
          (item.description && item.description.toLowerCase().includes(query)) ||
          (item.custom_tags && item.custom_tags.toLowerCase().includes(query)) ||
          (item.tags && Array.isArray(item.tags) && item.tags.some(tag => tag.toLowerCase().includes(query)))
        );
      });
      
      setFilteredItems(filtered);
      const endTime = performance.now();
      setSearchTime(Math.round(endTime - startTime));
      
      // Always show AI search option after basic search
      setShowAiSearchOption(true);
      
    } catch (error) {
      console.error('Search error:', error);
      setError('Failed to search items. Please try again.');
      setSearchTier('keyword'); // Reset to default on error
      setFilteredItems([]);
    } finally {
      setIsLoading(false);
      setIsSearching(false);
    }
  };

  // Handle AI search using hybrid endpoint
  const handleAiSearch = async () => {
    if (!lastSearchQuery) return;
    
    try {
      setIsLoading(true);
      setIsSearching(true);
      setShowAiSearchOption(false); // Hide AI option while AI search is in progress
      
      // Record start time for AI search
      const startTime = performance.now();
            // Use enhanced hybrid search for all searches  
        const endpoint = `/search?q=${encodeURIComponent(searchQuery)}`;
        console.log(`Using enhanced hybrid search endpoint: ${endpoint}`);
      
      const response = await apiInstance.get(endpoint);
      console.log('AI search response:', response.data);
      
      // Calculate search execution time
      const endTime = performance.now();
      const duration = Math.round(endTime - startTime);
      setSearchTime(duration);
      setSearchTier('llm'); // Mark as AI search
      
      // Process search results - hybrid search returns either an array or structured object
      let searchResults = [];
      
      if (response.data) {
        if (Array.isArray(response.data)) {
          searchResults = response.data;
        } else if (typeof response.data === 'object' && Array.isArray(response.data.items)) {
          searchResults = response.data.items;
        }
      }
      
      console.log(`AI search found ${searchResults.length} items`);
      setFilteredItems(searchResults);
      
    } catch (error) {
      console.error('AI search error:', error);
      setError('Failed to perform AI search. Please try again.');
      setSearchTier('keyword'); // Reset to basic on error
    } finally {
      setIsLoading(false);
      setIsSearching(false);
    }
  };
  
  // Handle search form submission
  const handleSearchSubmit = (value) => {
    // If it's coming from the search bar, check value type
    // Otherwise, it's called from a form submit event
    if (typeof value === 'string') {
      setSearchQuery(value);
      handleBasicSearch(); // Always start with basic search
    } else if (value && value.preventDefault) {
      value.preventDefault();
      handleBasicSearch(); // Always start with basic search
    } else {
      handleBasicSearch(); // Always start with basic search
    }
  };
  
  // Toggle item selection
  const toggleItemSelection = (itemId) => {
    if (selectedItems.includes(itemId)) {
      setSelectedItems(selectedItems.filter(id => id !== itemId));
    } else {
      setSelectedItems([...selectedItems, itemId]);
    }
  };
  
  // Handle item click - open modal instead of navigating
  const handleItemClick = (itemId) => {
    setSelectedItemId(itemId);
    setItemDetailOpen(true);
  };

  // Handle closing the item detail modal
  const handleCloseItemDetail = () => {
    setItemDetailOpen(false);
    setSelectedItemId(null); // Reset selected item ID when closing modal
  };

  // Toggle selection mode
  const toggleSelectMode = () => {
    setSelectMode(!selectMode);
    setSelectedItems([]);
  };
  
  // Select all items
  const selectAllItems = () => {
    if (selectedItems.length === filteredItems.length) {
      // If all are selected, deselect all
      setSelectedItems([]);
    } else {
      // Otherwise select all
      const allIds = filteredItems.map(item => item.id);
      setSelectedItems(allIds);
    }
  };
  
  // Delete selected items
  const deleteSelectedItems = async () => {
    if (!selectedItems.length) return;
    
    try {
      setIsLoading(true);
      
      // Delete each selected item
      for (const itemId of selectedItems) {
        await itemService.deleteItem(itemId);
      }
      
      // Refresh items list
      const updatedItems = items.filter(item => !selectedItems.includes(item.id));
      setItems(updatedItems);
      setFilteredItems(updatedItems);
      setSelectedItems([]);
      
      // Exit select mode
      setSelectMode(false);
    } catch (error) {
      console.error('Error deleting items:', error);
      setError('Failed to delete items. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Create the header search component
  const headerSearchComponent = (
    <Box sx={{ flexGrow: 1, mx: 1 }}>
      <EnhancedSearchBar
        value={searchQuery}
        onChange={handleSearchChange}
        onSubmit={handleSearchSubmit}
        searchTier={searchTier}
        searchTime={searchTime}
        isSearching={isSearching}
        showAiOption={showAiSearchOption}
        onAiSearchRequest={handleAiSearch}
        placeholder="Search items..."
        variant="outlined"
        size="small"
        elevation={0}
        sx={{ 
          backgroundColor: 'white',
          borderRadius: 1,
          border: '2px solid rgba(0, 0, 0, 0.1)',
          p: 0.5,
          '& .MuiInputBase-root': {
            color: 'rgba(0, 0, 0, 0.87)',
            height: 38,
            pl: 1
          },
          '&:hover': {
            border: '2px solid rgba(0, 0, 0, 0.23)'
          },
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)'
        }}
      />
    </Box>
  );
  
  return (
    <Layout title="All Items" headerContent={headerSearchComponent}>
      <Box sx={{ p: 2 }}>
        {/* Action button row - only show when not in select mode */}
        {!selectMode && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button 
              variant="outlined" 
              onClick={toggleSelectMode}
              startIcon={<SelectAllIcon />}
            >
              Select Items
            </Button>
          </Box>
        )}
        
        {/* Selection Controls - Only visible when in select mode */}
        {selectMode && (
          <Paper elevation={1} sx={{ p: 2, mb: 3, position: 'sticky', top: 0, zIndex: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Tooltip title={selectedItems.length === filteredItems.length ? 'Deselect All' : 'Select All'}>
                <Button 
                  variant="outlined"
                  onClick={selectAllItems}
                  startIcon={<SelectAllIcon />}
                >
                  {selectedItems.length === filteredItems.length ? 'Deselect All' : 'Select All'}
                </Button>
              </Tooltip>
              
              <Button 
                variant="contained" 
                color="error" 
                onClick={deleteSelectedItems}
                disabled={selectedItems.length === 0}
                startIcon={<DeleteIcon />}
              >
                Delete ({selectedItems.length})
              </Button>
            </Box>
          </Paper>
        )}
        
        {/* Main Content */}
        <Box sx={{ position: 'relative' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {isLoading ? (
            <LoadingOverlay />
          ) : (
            <>
              {/* Items Grid */}
              {filteredItems.length > 0 ? (
                <Grid container spacing={1}>
                  {filteredItems.map((item) => (
                    <Grid item xs={4} sm={3} md={2} key={item.id}>
                      <Card 
                        sx={{ 
                          height: '100%',
                          position: 'relative',
                          border: selectMode && selectedItems.includes(item.id) 
                            ? '2px solid #1976d2' 
                            : 'none'
                        }}
                      >
                        {selectMode && (
                          <Checkbox
                            checked={selectedItems.includes(item.id)}
                            onChange={() => toggleItemSelection(item.id)}
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              zIndex: 1,
                              color: 'white',
                              backgroundColor: 'rgba(0,0,0,0.3)',
                              borderRadius: '50%',
                              p: 0.5,
                              m: 0.5
                            }}
                          />
                        )}
                        
                        <CardActionArea 
                          onClick={() => handleItemClick(item.id)}
                          sx={{ height: '100%' }}
                        >
                          <Box 
                            sx={{
                              height: 0,
                              paddingTop: '100%', // 1:1 aspect ratio
                              position: 'relative',
                              backgroundColor: 'rgba(0,0,0,0.04)'
                            }}
                          >
                            <Box
                              component="img"
                              src={getBestItemImageUrl(item)}
                              alt={item.name || 'Item'}
                              loading="lazy"
                              decoding="async"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = placeholderImage;
                              }}
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                            />
                          </Box>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    {searchQuery ? 'No items match your search' : 'No items found'}
                  </Typography>
                  {!searchQuery && (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={() => navigate('/add')}
                      sx={{ mt: 2 }}
                    >
                      Add Items
                    </Button>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
        
        {/* Floating Action Button to add items */}
        {!isLoading && filteredItems.length > 0 && !selectMode && (
          <Fab
            color="primary"
            aria-label="add"
            sx={{ position: 'fixed', bottom: 76, right: 16 }} /* Adjusted for bottom nav */
            onClick={() => navigate('/add')}
          >
            <AddIcon />
          </Fab>
        )}
      </Box>
      
      {/* Item Detail Modal */}
      <ItemDetailModal 
        open={itemDetailOpen} 
        onClose={handleCloseItemDetail} 
        itemId={selectedItemId}
        onItemsAdded={(newItems) => {
          console.log(`${newItems.length} additional items created from group photo, refreshing data...`);
          fetchItems();
        }}
      />
    </Layout>
  );
};

export default AllItems;
